/**
 * Device detection utilities for server-side rendering
 */

// Device size constants - keeping local to avoid circular dependencies
const DEVICE_SIZES = {
  LARGE: "lg",
  SMALL: "sm",
} as const;

type DeviceSize = typeof DEVICE_SIZES.LARGE | typeof DEVICE_SIZES.SMALL;

/**
 * Detects device type from Akamai x-akamai-device-characteristics header
 *
 * @param headers - Request headers object
 * @returns Device size ("lg" | "sm"). Falls back to user-agent when Akamai header is missing (e.g. localhost).
 *
 * @example
 * ```typescript
 * import { detectDeviceFromAkamaiHeader } from "@tm/utility";
 *
 * export async function getServerSideProps(context) {
 *   const { req } = context;
 *   const deviceType = detectDeviceFromAkamaiHeader(req.headers);
 *
 *   return {
 *     props: { deviceType }
 *   };
 * }
 * ```
 */
const MOBILE_USER_AGENT_PATTERNS =
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;

/**
 * Fallback when Akamai header is missing (e.g. localhost). Uses user-agent to detect mobile.
 */
function getDeviceFromUserAgent(
  headers: Record<string, string | string[]>
): DeviceSize {
  const ua = headers["user-agent"];
  const uaStr = Array.isArray(ua) ? ua[0] : ua || "";
  return MOBILE_USER_AGENT_PATTERNS.test(uaStr)
    ? DEVICE_SIZES.SMALL
    : DEVICE_SIZES.LARGE;
}

export function detectDeviceFromAkamaiHeader(
  headers: Record<string, string | string[]> = {}
): DeviceSize {
  const akamaiDeviceHeader = headers["x-akamai-device-characteristics"];

  if (!akamaiDeviceHeader) {
    return getDeviceFromUserAgent(headers);
  }

  // Handle both string and string[] cases
  const headerValue = Array.isArray(akamaiDeviceHeader)
    ? akamaiDeviceHeader[0]
    : akamaiDeviceHeader;

  if (headerValue?.includes("is_mobile=true")) {
    return DEVICE_SIZES.SMALL;
  } else {
    return DEVICE_SIZES.LARGE;
  }
}

// Export the constants for convenience
export { DEVICE_SIZES };
